import { useEffect } from "react";
import { useZero } from "@rocicorp/zero/react";
import { queries } from "../lib/queries.js";
import { DISABLE_PRELOAD } from "../lib/debug-flags.js";

/**
 * Toggle in DevTools console:
 *   localStorage.setItem('ano-debug-preload', '1') // enable
 *   localStorage.removeItem('ano-debug-preload')   // disable
 * Reload to reset internal counters. Cheap call; runs once per event.
 */
function debugLog(...args: unknown[]) {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem("ano-debug-preload") !== "1") return;

  console.log(...args);
}

/** Warm the browser's image cache for a set of URLs. */
function preloadImages(urls: string[]) {
  for (const url of urls) {
    const img = new Image();
    img.src = url;
  }
}

/**
 * Workspace-wide message preload — populates Zero's local SQLite
 * cache with recent message bodies + sender for every channel the
 * caller can access in the active workspace, plus all DMs.
 *
 * Replaces the prior 15 per-channel `messages.byChannelComposite`
 * preloads with ONE subscription per active scope. Saves up to 14
 * always-on Zero pipelines per active user.
 *
 * `workspaceId` semantics:
 *   • string  → workspace channels in that workspace + all DMs
 *   • null    → DMs only (Home scope — no workspace selected)
 * Both cases hit the same shared query (`byWorkspaceWithRecentMessages`);
 * the null arg branches the where clause inside the query so DMs
 * preload regardless of whether a workspace is in scope.
 *
 * The active channel still opens its own `byChannelComposite` (rich
 * joins: reactions, attachments, link previews, etc.) when the user
 * focuses a channel. Different query identity from this preload, so
 * the active query opens its own server pipeline — but the local
 * cache from this preload provides a prefix of those messages, so
 * first paint is instant. Server fills the gap (messages 51-300 +
 * rich joins) within ~100-200ms.
 *
 * Fires whenever `workspaceId` changes (workspace switch, including
 * Home ↔ workspace transitions). Cleanup runs on switch and on
 * unmount. Survives React 19 StrictMode's mount→unmount→remount
 * cycle by re-establishing the subscription on every mount.
 */
export function usePreloadChannels(workspaceId: string | null) {
  const zero = useZero();

  useEffect(() => {
    if (DISABLE_PRELOAD) return;

    const t0 = performance.now();
    debugLog(
      `[preload] subscribe workspace=${workspaceId ?? "null(home/dms)"}`,
    );

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    try {
      const preload = zero.preload(
        queries.channels.byWorkspaceWithRecentMessages(workspaceId),
      );
      cleanup = preload.cleanup;

      // After the primary preload completes, do a one-shot run of the
      // same query to extract image URLs for browser HTTP cache
      // warming. Same query identity → shares materialization with
      // the live preload above. Walks the full nested shape:
      // channels[].messages[].sender.avatar_url.
      void preload.complete.then(async () => {
        if (cancelled) return;
        try {
          const channels = await zero.run(
            queries.channels.byWorkspaceWithRecentMessages(workspaceId),
          );
          if (cancelled) return;
          let totalMessages = 0;
          const urls: string[] = [];
          for (const ch of channels) {
            const messages = ((ch as Record<string, unknown>).messages ??
              []) as Array<Record<string, unknown>>;
            totalMessages += messages.length;
            for (const msg of messages) {
              const sender = msg.sender as Record<string, unknown> | null;
              if (sender?.avatar_url) urls.push(sender.avatar_url as string);
            }
          }
          debugLog(
            `[preload] complete workspace=${workspaceId ?? "null(home/dms)"} ` +
              `channels=${channels.length} messages=${totalMessages} ` +
              `avatars=${new Set(urls).size} elapsed=${Math.round(performance.now() - t0)}ms`,
          );
          if (!cancelled) {
            preloadImages([...new Set(urls)]);
          }
        } catch {
          // Best-effort
        }
      });
    } catch {
      // Silently skip — preload is best-effort
    }

    return () => {
      cancelled = true;
      cleanup?.();
      debugLog(
        `[preload] cleanup workspace=${workspaceId ?? "null(home/dms)"}`,
      );
    };
  }, [zero, workspaceId]);
}
